import { useState } from 'react'
import { MemoryError, setMemoryItem, type ItemAction, type MemoryKind, type RecordMemory } from 'brief/memoryApi'
import { memorySections, type MemoryRowView } from 'brief/memoryView'

/**
 * Memory - what stays true about this customer between conversations: what they want, what they
 * asked, what we promised, what worries them. Each row says where it came from; hovering the source
 * shows the customer's or colleague's own words. The AI proposes rows (marked "suggested" until
 * someone confirms them); a person can confirm, reword, close, reopen, or add their own - none of
 * which costs anything.
 */

/** What closing a row is called, per kind - "kept" for a promise, "answered" for a question. */
const DONE_LABEL: Record<MemoryKind, string> = {
  question: 'Answered', promise: 'Kept', want: 'Met', objection: 'Resolved', fact: 'Done',
}

const KIND_OPTIONS: Array<{ kind: MemoryKind; label: string }> = [
  { kind: 'want', label: 'What they want' },
  { kind: 'question', label: 'A question they asked' },
  { kind: 'promise', label: 'Something we promised' },
  { kind: 'objection', label: 'A concern' },
  { kind: 'fact', label: 'Worth knowing' },
]

const MAX_TEXT = 400

function Row({
  row,
  canEdit,
  busy,
  onChange,
}: {
  row: MemoryRowView
  canEdit: boolean
  busy: boolean
  onChange: (change: ItemAction) => void
}) {
  const { item } = row
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(item.text)
  const [dueOn, setDueOn] = useState(item.dueOn ?? '')

  if (editing) {
    return (
      <li className="memory-row is-editing">
        <textarea className="memory-input" value={text} maxLength={MAX_TEXT} rows={2} onChange={(e) => setText(e.target.value)} autoFocus />
        {item.kind === 'promise' && (
          <label className="memory-due">
            By <input type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />
          </label>
        )}
        <div className="memory-actions">
          <button
            type="button"
            className="btn btn-sm"
            disabled={busy || text.trim() === ''}
            onClick={() => {
              onChange({ action: 'edit', itemId: item.id, text: text.trim(), dueOn: dueOn || undefined })
              setEditing(false)
            }}
          >
            Save
          </button>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditing(false)}>
            Cancel
          </button>
        </div>
      </li>
    )
  }

  return (
    <li className={`memory-row${row.closed ? ' is-closed' : ''}`}>
      <div className="memory-main">
        <span className="memory-text">
          {item.text}
          {row.suggested && <span className="memory-suggested" title="The AI read proposed this. Confirm it once you have checked it.">suggested</span>}
        </span>
        <span className="brief-source" title={item.sourceQuote ? `“${item.sourceQuote}”` : undefined}>
          {row.state && <span className={`brief-pill is-${row.tone === 'neutral' ? 'quiet' : row.tone}`}>{row.state}</span>}
          {row.source}
          {item.sourceQuote && <span className="memory-quote-hint"> · hover for their words</span>}
          {row.closed && item.closedNote ? ` · ${item.closedNote}` : ''}
          {row.closed && item.closedByAi ? ' · closed by the AI read' : ''}
        </span>
      </div>
      {canEdit && (
        <div className="memory-actions">
          {row.closed ? (
            <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => onChange({ action: 'reopen', itemId: item.id })}>
              Reopen
            </button>
          ) : (
            <>
              {row.suggested && (
                <button type="button" className="btn btn-sm" disabled={busy} onClick={() => onChange({ action: 'confirm', itemId: item.id })}>
                  ✓ Confirm
                </button>
              )}
              <button type="button" className="btn btn-sm" disabled={busy} onClick={() => onChange({ action: 'close', itemId: item.id, status: 'done' })}>
                {DONE_LABEL[item.kind]}
              </button>
              <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => setEditing(true)}>
                Edit
              </button>
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                disabled={busy}
                title="No longer applies"
                onClick={() => onChange({ action: 'close', itemId: item.id, status: 'dropped' })}
              >
                Drop
              </button>
            </>
          )}
        </div>
      )}
    </li>
  )
}

function AddForm({ busy, onAdd, onCancel }: { busy: boolean; onAdd: (change: ItemAction) => void; onCancel: () => void }) {
  const [kind, setKind] = useState<MemoryKind>('want')
  const [text, setText] = useState('')
  const [dueOn, setDueOn] = useState('')
  return (
    <div className="memory-add">
      <select value={kind} onChange={(e) => setKind(e.target.value as MemoryKind)} aria-label="Kind">
        {KIND_OPTIONS.map((o) => (
          <option key={o.kind} value={o.kind}>
            {o.label}
          </option>
        ))}
      </select>
      <textarea
        className="memory-input"
        value={text}
        maxLength={MAX_TEXT}
        rows={2}
        placeholder="One plain sentence, so someone who never met this customer understands it"
        onChange={(e) => setText(e.target.value)}
        autoFocus
      />
      {kind === 'promise' && (
        <label className="memory-due">
          By <input type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />
        </label>
      )}
      <div className="memory-actions">
        <button type="button" className="btn btn-sm" disabled={busy || text.trim() === ''} onClick={() => onAdd({ action: 'add', kind, text: text.trim(), dueOn: dueOn || undefined })}>
          Add
        </button>
        <button type="button" className="btn btn-sm btn-ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  )
}

export function MemorySection({
  id,
  entityType,
  entityId,
  memory,
  loading,
  error,
  canEdit,
  onChanged,
  signedOut = false,
  onSignIn,
}: {
  id: string
  entityType: 'lead' | 'enquiry'
  entityId: string
  memory: RecordMemory | null
  loading: boolean
  error: string | null
  /** The record's follow-up permission decides who may change its memory. */
  canEdit: boolean
  onChanged: () => void
  /** The service refused the sign-in; the only fix is to sign in again, so offer that here. */
  signedOut?: boolean
  onSignIn?: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [adding, setAdding] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const change = async (what: ItemAction) => {
    setBusy(true)
    setActionError(null)
    try {
      await setMemoryItem(entityType, entityId, what)
      setAdding(false)
      onChanged()
    } catch (err) {
      setActionError(err instanceof MemoryError ? err.message : 'That could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  if (loading && !memory) return <div className="inline-loader" id={id}>Reading what we know about this customer…</div>
  if (error && !memory)
    return (
      <p className="brief-unavailable ai-error" id={id}>
        {signedOut
          ? 'Memory could not be read: the memory service did not accept your sign-in - it has most likely timed out. Everything else on this page still works because it uses a shared key; Memory and AI use yours.'
          : `Memory could not be read: ${error}`}
        {signedOut && onSignIn && (
          <button type="button" className="btn btn-sm" onClick={onSignIn}>
            Sign in again
          </button>
        )}
      </p>
    )

  const sections = memorySections(memory?.items ?? [], new Date())
  const nothing = (memory?.items ?? []).length === 0

  return (
    <div className="memory" id={id}>
      {memory?.summary && <p className="memory-summary">{memory.summary}</p>}

      {nothing ? (
        <p className="brief-none">
          Nothing is remembered about this customer yet. “Ask AI” reads the history and proposes what is worth keeping
          {canEdit ? ' — or add it yourself.' : '.'}
        </p>
      ) : (
        <div className="memory-grid">
          {sections.map((section) => (
            <div key={section.kind} className="memory-section">
              <span className="brief-label">{section.title}</span>
              {section.rows.length > 0 ? (
                <ul className="memory-list">
                  {section.rows.map((row) => (
                    <Row key={row.item.id} row={row} canEdit={canEdit} busy={busy} onChange={change} />
                  ))}
                </ul>
              ) : (
                <p className="brief-none">{section.empty}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {actionError && <p className="brief-unavailable">{actionError}</p>}

      {canEdit &&
        (adding ? (
          <AddForm busy={busy} onAdd={change} onCancel={() => setAdding(false)} />
        ) : (
          <button type="button" className="btn btn-sm memory-add-btn" onClick={() => setAdding(true)}>
            + Add to memory
          </button>
        ))}
    </div>
  )
}
